import { useState } from "react";
import axios from "axios";
import { AlertCircle, Clock, Loader2, Search, ShieldCheck } from "lucide-react";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const strings = {
  fr: {
    title: "Vérification KYC",
    sub: "Vérifiez l'authenticité d'une attestation KYC émise par Columbus Management Consulting GmbH en saisissant sa référence.",
    label: "Numéro de référence",
    placeholder: "CMC-KYC-2026-0001",
    button: "Vérifier",
    checking: "Vérification…",
    verified: "Attestation vérifiée",
    pending: "Vérification en cours",
    notfound: "Référence introuvable. Vérifiez le numéro saisi.",
    error: "Service temporairement indisponible. Réessayez plus tard.",
    nameLabel: "Titulaire",
    companyLabel: "Société",
    dateLabel: "Vérifiée le",
    note: "Aucune donnée saisie n'est conservée ni transmise à des tiers.",
  },
  en: {
    title: "KYC Verification",
    sub: "Verify the authenticity of a KYC attestation issued by Columbus Management Consulting GmbH by entering its reference.",
    label: "Reference number",
    placeholder: "CMC-KYC-2026-0001",
    button: "Verify",
    checking: "Verifying…",
    verified: "Attestation verified",
    pending: "Verification in progress",
    notfound: "Reference not found. Please check the number entered.",
    error: "Service temporarily unavailable. Please try again later.",
    nameLabel: "Holder",
    companyLabel: "Company",
    dateLabel: "Verified on",
    note: "No entered data is stored or shared with third parties.",
  },
  de: {
    title: "KYC-Verifizierung",
    sub: "Prüfen Sie die Echtheit einer von der Columbus Management Consulting GmbH ausgestellten KYC-Bescheinigung anhand ihrer Referenz.",
    label: "Referenznummer",
    placeholder: "CMC-KYC-2026-0001",
    button: "Prüfen",
    checking: "Wird geprüft…",
    verified: "Bescheinigung verifiziert",
    pending: "Verifizierung läuft",
    notfound: "Referenz nicht gefunden. Bitte prüfen Sie die eingegebene Nummer.",
    error: "Dienst vorübergehend nicht verfügbar. Bitte später erneut versuchen.",
    nameLabel: "Inhaber",
    companyLabel: "Gesellschaft",
    dateLabel: "Verifiziert am",
    note: "Eingegebene Daten werden weder gespeichert noch an Dritte weitergegeben.",
  },
};

const inputCls =
  "w-full border border-[#1E4437] bg-[#102920] px-4 py-3.5 font-mono text-sm uppercase tracking-[0.12em] text-[#FBF8F3] placeholder:text-[#B0C2B9]/40 placeholder:normal-case outline-none transition-colors duration-300 focus:border-[#C87D55]";

export const KycVerify = () => {
  const { lang } = useLang();
  const s = strings[lang] || strings.fr;
  const [reference, setReference] = useState("");
  const [state, setState] = useState({ status: "idle", data: null });

  const verify = async (e) => {
    e.preventDefault();
    if (!reference.trim()) return;
    setState({ status: "loading", data: null });
    try {
      const { data } = await axios.post(`${API}/kyc/verify`, { reference: reference.trim() });
      setState({ status: "ok", data });
    } catch (err) {
      setState({ status: err.response?.status === 404 ? "notfound" : "error", data: null });
    }
  };

  return (
    <div className="border border-[#1E4437] bg-[#102920] p-8 sm:p-10" data-testid="kyc-verify-box">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center border border-[#C87D55]/40 text-[#C87D55]">
          <ShieldCheck className="h-4 w-4" />
        </span>
        <h2 className="font-display text-xl font-semibold text-[#FBF8F3]">{s.title}</h2>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-[#B0C2B9]">{s.sub}</p>

      <form onSubmit={verify} className="mt-6" data-testid="kyc-verify-form">
        <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#B0C2B9]">
          {s.label}
        </label>
        <div className="flex gap-3">
          <input
            data-testid="kyc-reference-input"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder={s.placeholder}
            className={inputCls}
          />
          <button
            data-testid="kyc-verify-button"
            type="submit"
            disabled={state.status === "loading"}
            className="flex shrink-0 items-center gap-2 bg-[#C87D55] px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-[#FBF8F3] transition-colors duration-300 hover:bg-[#FBF8F3] hover:text-[#0A1F18] disabled:opacity-50"
          >
            {state.status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            {state.status === "loading" ? s.checking : s.button}
          </button>
        </div>
      </form>

      {state.status === "ok" && state.data && (
        <div
          data-testid="kyc-result"
          className={`mt-6 border p-5 ${
            state.data.status === "verified"
              ? "border-emerald-500/40 bg-emerald-500/10"
              : "border-[#C87D55]/40 bg-[#C87D55]/10"
          }`}
        >
          <div className="flex items-center gap-2">
            {state.data.status === "verified" ? (
              <ShieldCheck className="h-4 w-4 text-emerald-400" />
            ) : (
              <Clock className="h-4 w-4 text-[#C87D55]" />
            )}
            <span
              data-testid="kyc-result-status"
              className={`font-mono text-[11px] uppercase tracking-[0.2em] ${
                state.data.status === "verified" ? "text-emerald-400" : "text-[#C87D55]"
              }`}
            >
              {state.data.status === "verified" ? s.verified : s.pending}
            </span>
          </div>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-[#B0C2B9]">{s.nameLabel}</span>
              <span className="text-[#FBF8F3]" data-testid="kyc-result-name">{state.data.name}</span>
            </div>
            {state.data.company && (
              <div className="flex justify-between gap-4">
                <span className="text-[#B0C2B9]">{s.companyLabel}</span>
                <span className="text-[#FBF8F3]">{state.data.company}</span>
              </div>
            )}
            {state.data.verified_at && (
              <div className="flex justify-between gap-4">
                <span className="text-[#B0C2B9]">{s.dateLabel}</span>
                <span className="font-mono text-[#FBF8F3]">{state.data.verified_at}</span>
              </div>
            )}
            <div className="flex justify-between gap-4 border-t border-white/10 pt-2">
              <span className="text-[#B0C2B9]">{s.label}</span>
              <span className="font-mono text-[#C87D55]">{state.data.reference}</span>
            </div>
          </div>
        </div>
      )}

      {(state.status === "notfound" || state.status === "error") && (
        <p
          data-testid="kyc-result-error"
          className="mt-6 flex items-center gap-2 border border-[#D8232A]/40 bg-[#D8232A]/10 px-4 py-3 text-sm text-[#F87171]"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          {state.status === "notfound" ? s.notfound : s.error}
        </p>
      )}

      <p className="mt-6 font-mono text-[10px] leading-relaxed tracking-[0.1em] text-[#B0C2B9]/60">{s.note}</p>
    </div>
  );
};
